/**
 * Map generated GraphQL permission types (ENG-180 W5) to/from the webapp's
 * permission model. The API uses UPPERCASE GraphQL enum names; the webapp uses
 * the design's lowercase kinds (`byType`, `property-name`, …). Kept faithful to
 * the API's decode/encode so client preview == server authority.
 */

import {
	type CreatePermissionRuleInput,
	type EffectiveNodeAccessQuery,
	PermissionLevel as GqlLevel,
	PermissionScopeKind as GqlScopeKind,
	PermissionSource as GqlSource,
	PermissionSubjectKind as GqlSubjectKind,
	type PermissionRulesQuery,
	type PermissionScopeInput,
	type PermissionSubjectsQuery,
	type PreviewSubjectInput,
	type UpdatePermissionRuleInput,
} from "../../graphql/generated/types.gen";
import type {
	AccessResult,
	Level,
	NodeAccess,
	PeopleData,
	Person,
	QueryExpr,
	Role,
	Rule,
	RuleSource,
	Scope,
	Subject,
	Team,
} from "../types";

type GqlRule = PermissionRulesQuery["permissionRules"][number];
type GqlScope = GqlRule["scope"];
type GqlSubjects = PermissionSubjectsQuery["permissionSubjects"];

// ── Level ─────────────────────────────────────────────────────────────────────

const GQL_TO_LEVEL: Record<GqlLevel, Level> = {
	[GqlLevel.None]: "none",
	[GqlLevel.Read]: "read",
	[GqlLevel.Edit]: "edit",
	[GqlLevel.Full]: "full",
};
const LEVEL_TO_GQL: Record<Level, GqlLevel> = {
	none: GqlLevel.None,
	read: GqlLevel.Read,
	edit: GqlLevel.Edit,
	full: GqlLevel.Full,
};

/** Map a server-authoritative GraphQL level to the webapp's lowercase Level. */
export function gqlLevelToLevel(level: GqlLevel): Level {
	return GQL_TO_LEVEL[level];
}

// ── Subject ───────────────────────────────────────────────────────────────────

function gqlToSubject(
	kind: GqlSubjectKind,
	id: string | null | undefined,
): Subject {
	switch (kind) {
		case GqlSubjectKind.Everyone:
			return { kind: "everyone" };
		case GqlSubjectKind.User:
			return { kind: "user", id: id ?? "" };
		case GqlSubjectKind.Team:
			return { kind: "team", id: id ?? "" };
		case GqlSubjectKind.Role:
			return { kind: "role", id: id ?? "" };
	}
}

function subjectToGql(subject: Subject): {
	subjectKind: GqlSubjectKind;
	subjectId: string | null;
} {
	switch (subject.kind) {
		case "everyone":
			return { subjectKind: GqlSubjectKind.Everyone, subjectId: null };
		case "user":
			return { subjectKind: GqlSubjectKind.User, subjectId: subject.id };
		case "team":
			return { subjectKind: GqlSubjectKind.Team, subjectId: subject.id };
		case "role":
			return { subjectKind: GqlSubjectKind.Role, subjectId: subject.id };
	}
}

/**
 * Map a webapp Subject to the `effectiveNodeAccess` query's PreviewSubjectInput
 * (server-authoritative preview-as).
 */
export function subjectToPreviewInput(subject: Subject): PreviewSubjectInput {
	const { subjectKind, subjectId } = subjectToGql(subject);
	return { kind: subjectKind, id: subjectId };
}

// ── Scope ─────────────────────────────────────────────────────────────────────

// Partial: the API may still send relationship-type-scoped scope kinds (edge
// and edge-property, keyed off the relationship type rather than an id) that
// the webapp model has dropped ahead of the API's own removal — see
// gqlToScope's fallback below. Kinds absent here resolve to `undefined`.
const GQL_TO_SCOPE_KIND: Partial<Record<GqlScopeKind, Scope["kind"]>> = {
	[GqlScopeKind.Global]: "global",
	[GqlScopeKind.Node]: "node",
	[GqlScopeKind.ByType]: "byType",
	[GqlScopeKind.ByTag]: "byTag",
	[GqlScopeKind.Query]: "query",
	[GqlScopeKind.Property]: "property",
	[GqlScopeKind.PropertyName]: "property-name",
	[GqlScopeKind.Edge]: "edge",
	[GqlScopeKind.EdgeProp]: "edgeProp",
};

/** `null` for a scope kind the webapp model no longer represents (see gqlToRule). */
function gqlToScope(s: GqlScope): Scope | null {
	switch (GQL_TO_SCOPE_KIND[s.kind]) {
		case "global":
			return { kind: "global" };
		case "node":
			return { kind: "node", nodeId: s.nodeId ?? "" };
		case "byType":
			return { kind: "byType", nodeType: s.nodeType ?? "" };
		case "byTag":
			return { kind: "byTag", tag: s.tag ?? "" };
		case "query":
			return {
				kind: "query",
				expr: (s.predicate as QueryExpr | null) ?? {
					all: true,
					conditions: [],
				},
			};
		case "property":
			return {
				kind: "property",
				nodeId: s.nodeId ?? "",
				propId: s.propId ?? "",
			};
		case "property-name":
			return { kind: "property-name", name: s.name ?? "" };
		case "edge":
			return { kind: "edge", edgeId: s.edgeId ?? "" };
		case "edgeProp":
			return {
				kind: "edgeProp",
				edgeId: s.edgeId ?? "",
				propId: s.propId ?? "",
			};
		default:
			return null;
	}
}

function scopeToGqlInput(scope: Scope): PermissionScopeInput {
	switch (scope.kind) {
		case "global":
			return { kind: GqlScopeKind.Global };
		case "node":
			return { kind: GqlScopeKind.Node, nodeId: scope.nodeId };
		case "byType":
			return { kind: GqlScopeKind.ByType, nodeType: scope.nodeType };
		case "byTag":
			return { kind: GqlScopeKind.ByTag, tag: scope.tag };
		case "query":
			return { kind: GqlScopeKind.Query, predicate: scope.expr };
		case "property":
			return {
				kind: GqlScopeKind.Property,
				nodeId: scope.nodeId,
				propId: scope.propId,
			};
		case "property-name":
			return { kind: GqlScopeKind.PropertyName, name: scope.name };
		case "edge":
			return { kind: GqlScopeKind.Edge, edgeId: scope.edgeId };
		case "edgeProp":
			return {
				kind: GqlScopeKind.EdgeProp,
				edgeId: scope.edgeId,
				propId: scope.propId,
			};
	}
}

// ── Source (derived — API stores system|default|user) ────────────────────────

function deriveSource(
	source: GqlSource,
	subject: Subject,
	scope: Scope,
): RuleSource {
	if (source === GqlSource.Default) return "default";
	if (source === GqlSource.System) return "system";
	if (scope.kind === "query") return "query";
	if (
		scope.kind === "node" ||
		scope.kind === "property" ||
		scope.kind === "edge" ||
		scope.kind === "edgeProp"
	) {
		return "direct";
	}
	if (subject.kind === "role") return "role";
	return "rule";
}

// ── Rule ──────────────────────────────────────────────────────────────────────

/**
 * `null` when the server sends a relationship-type-scoped scope kind the
 * webapp model no longer represents, ahead of the API's own removal —
 * skip-with-warning rather than throwing, since a handful of such rules can
 * still be in flight until that lands.
 */
export function gqlToRule(r: GqlRule): Rule | null {
	const subject = gqlToSubject(r.subjectKind, r.subjectId);
	const scope = gqlToScope(r.scope);
	if (scope === null) {
		console.warn(
			`Skipping permission rule "${r.name}" (${r.id}): unsupported scope kind "${r.scope.kind}"`,
		);
		return null;
	}
	return {
		id: r.id,
		name: r.name,
		subject,
		level: GQL_TO_LEVEL[r.level],
		scope,
		priority: r.priority,
		source: deriveSource(r.source, subject, scope),
		locked: r.locked,
		desc: r.description ?? undefined,
	};
}

export function ruleToCreateInput(
	input: Omit<Rule, "id">,
): CreatePermissionRuleInput {
	const { subjectKind, subjectId } = subjectToGql(input.subject);
	return {
		name: input.name,
		subjectKind,
		subjectId,
		level: LEVEL_TO_GQL[input.level],
		scope: scopeToGqlInput(input.scope),
		priority: input.priority,
		description: input.desc ?? null,
	};
}

export function patchToUpdateInput(
	patch: Partial<Rule>,
): UpdatePermissionRuleInput {
	const out: UpdatePermissionRuleInput = {};
	if (patch.name !== undefined) out.name = patch.name;
	if (patch.level !== undefined) out.level = LEVEL_TO_GQL[patch.level];
	if (patch.priority !== undefined) out.priority = patch.priority;
	if (patch.desc !== undefined) out.description = patch.desc;
	if (patch.subject !== undefined) {
		const { subjectKind, subjectId } = subjectToGql(patch.subject);
		out.subjectKind = subjectKind;
		out.subjectId = subjectId;
	}
	if (patch.scope !== undefined) out.scope = scopeToGqlInput(patch.scope);
	return out;
}

// ── Effective access (server-authoritative preview-as) ───────────────────────

type GqlEffectiveAccess = NonNullable<
	EffectiveNodeAccessQuery["effectiveNodeAccess"]
>;

/**
 * Build the resolver's `NodeAccess` map from the server's `effectiveNodeAccess`
 * result, so preview-as is server-authoritative for the node, its properties, its
 * edges, and edge-properties alike. Keyed to match the client resolver + sidebar
 * lookups: properties by KEY, edges by id, edgeProps by `${edgeId}:${key}`. The
 * `rule`/`applicable` chain is empty — the server returns the resolved level only.
 */
export function effectiveAccessToNodeAccess(a: GqlEffectiveAccess): NodeAccess {
	const at = (level: GqlLevel): AccessResult => ({
		level: gqlLevelToLevel(level),
		rule: null,
		applicable: [],
	});
	const properties: NodeAccess["properties"] = {};
	for (const p of a.properties) properties[p.key] = at(p.access);
	const edges: NodeAccess["edges"] = {};
	const edgeProps: NodeAccess["edgeProps"] = {};
	for (const e of a.edges) {
		edges[e.edgeId] = at(e.access);
		for (const ep of e.properties) {
			edgeProps[`${e.edgeId}:${ep.key}`] = at(ep.access);
		}
	}
	return { node: at(a.level), properties, edges, edgeProps };
}

// ── People + Viewer ───────────────────────────────────────────────────────────

const PALETTE = [
	"#C9A227",
	"#B5503C",
	"#5E7D62",
	"#C9922E",
	"#E5C77A",
	"#8C6A3D",
	"#D4785A",
	"#A8A08C",
];

function colorOf(id: string): string {
	let h = 0;
	for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
	return PALETTE[h % PALETTE.length];
}

function initialsOf(name: string): string {
	const parts = name.trim().split(/\s+/);
	return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase() || "?";
}

export const EMPTY_PEOPLE: PeopleData = {
	users: [],
	teams: [],
	roles: [],
	userRoles: {},
};

export function gqlToPeopleData(s: GqlSubjects): PeopleData {
	const users: Person[] = s.users.map((u) => ({
		id: u.id,
		name: u.name,
		initials: initialsOf(u.name),
		color: colorOf(u.id),
	}));
	const teams: Team[] = s.teams.map((t) => ({
		id: t.id,
		name: t.name,
		color: t.color ?? colorOf(t.id),
		memberIds: t.memberIds,
	}));
	const roles: Role[] = s.roles.map((r) => ({
		id: r.id,
		name: r.name,
		description: r.description ?? "",
		color: r.color ?? colorOf(r.id),
	}));
	const userRoles: Record<string, string> = {};
	for (const u of s.users) if (u.roleId) userRoles[u.id] = u.roleId;
	return { users, teams, roles, userRoles };
}
